import { useState } from "react";
import { Input } from "@/components/ui/input";
import { FileText, Check, XCircle, Pencil, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Reusable template chip strip. Renders every saved SMS broadcast template
 * as a compact pill; hovering reveals rename + delete affordances.
 */
export function TemplateStrip({ templates, setTemplates, onApply }) {
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const startRename = (t, e) => {
    e.stopPropagation();
    setRenamingId(t.id);
    setRenameValue(t.name || "");
  };

  const saveRename = async (tid) => {
    try {
      await api.patch(`/ghl/sms-broadcast-templates/${tid}`, {
        name: renameValue.trim() || null,
      });
      setTemplates((prev) => prev.map((t) =>
        t.id === tid ? { ...t, name: renameValue.trim() || null } : t
      ));
      setRenamingId(null);
      toast.success("Renamed");
    } catch { toast.error("Rename failed"); }
  };

  const deleteTemplate = async (tid, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/ghl/sms-broadcast-templates/${tid}`);
      setTemplates((prev) => prev.filter((t) => t.id !== tid));
      toast.success("Template removed");
    } catch { toast.error("Delete failed"); }
  };

  if (templates.length === 0) return null;
  return (
    <div className="mb-4" data-testid="sms-broadcast-templates">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
        <FileText className="h-3 w-3" /> Recent templates ({templates.length})
      </div>
      <div className="flex flex-wrap gap-1.5">
        {templates.map((t) => (
          renamingId === t.id ? (
            <div key={t.id}
                 className="flex items-center gap-1 rounded-full border border-orange-500/40 bg-orange-500/10 pl-3 pr-1 py-0.5"
                 data-testid={`sms-broadcast-template-rename-${t.id}`}>
              <Input autoFocus value={renameValue}
                     onChange={(e) => setRenameValue(e.target.value)}
                     placeholder="Template name" maxLength={40}
                     className="h-6 py-0 px-2 text-xs bg-transparent border-0 focus-visible:ring-0 w-40"
                     data-testid={`sms-broadcast-template-name-input-${t.id}`}
                     onKeyDown={(e) => {
                       if (e.key === "Enter") saveRename(t.id);
                       if (e.key === "Escape") setRenamingId(null);
                     }} />
              <button onClick={() => saveRename(t.id)}
                      className="text-emerald-300 hover:text-emerald-200 p-1"
                      data-testid={`sms-broadcast-template-save-name-${t.id}`} title="Save">
                <Check className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => setRenamingId(null)}
                      className="text-zinc-400 hover:text-zinc-200 p-1"
                      data-testid={`sms-broadcast-template-cancel-name-${t.id}`} title="Cancel">
                <XCircle className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <button key={t.id} onClick={() => onApply(t)}
                    className="group flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] hover:bg-orange-500/10 hover:border-orange-500/30 px-3 py-1 text-xs text-left transition"
                    data-testid={`sms-broadcast-template-${t.id}`} title={t.body}>
              <span className={`max-w-[220px] truncate ${t.name ? "text-orange-300 font-medium" : ""}`}>
                {t.name ? t.name : (t.body.slice(0, 60) + (t.body.length > 60 ? "…" : ""))}
              </span>
              <span className="text-[10px] text-zinc-500">×{t.use_count}</span>
              <Pencil className="h-3 w-3 text-zinc-500 hover:text-orange-300 opacity-0 group-hover:opacity-100 transition"
                      onClick={(e) => startRename(t, e)}
                      data-testid={`sms-broadcast-template-rename-btn-${t.id}`} />
              <X className="h-3 w-3 text-zinc-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition"
                 onClick={(e) => deleteTemplate(t.id, e)}
                 data-testid={`sms-broadcast-template-delete-${t.id}`} />
            </button>
          )
        ))}
      </div>
    </div>
  );
}
